import { Requirement, Select, CourseSet } from "../../types/requirement.response";
import { CourseSummary } from "../../types/course.response";
import { getCoursesFromCourseSet, getGroupRepresentatives } from "../../utils/requirementResponseUtils";
import { sortCourses } from "../../utils/sortUtils";

/** 一个面板对应一个可展示节点:LIST/OPTION 的面板是 CourseSet,GROUP/OPTION_GROUP 的面板是 Select。 */
export type PanelNode = Select | CourseSet;

export interface RequirementPanels {
    /** true = 有 pills(OPTION/OPTION_GROUP,多面板);false = 单面板(LIST/GROUP),不渲染 pills/进度 */
    tabbed: boolean;
    panels: PanelNode[];
}

/**
 * 把 requirement 拆成「面板」+ 是否分页(pills)。
 * 课程怎么算由面板节点的 type 决定(见 buildPanelRows),不再依赖 uiType。
 */
export function getRequirementPanels(requirement: Requirement): RequirementPanels {
    const root = requirement.rootNode;
    switch (requirement.info.uiType) {
        case "LIST":
            // 根节点就是一个 CourseSet,单面板
            return { tabbed: false, panels: [root] };
        case "GROUP":
            // 根节点是 Select,单面板(按组代表课展示)
            return { tabbed: false, panels: [root] };
        case "OPTION":
            return {
                tabbed: true,
                panels: (root as Select).children.filter(
                    (child): child is CourseSet => child.type === "COURSE_SET"
                ),
            };
        case "OPTION_GROUP":
            return {
                tabbed: true,
                panels: (root as Select).children.filter(
                    (child): child is Select => child.type === "SELECT"
                ),
            };
        default:
            return { tabbed: false, panels: [] };
    }
}

export interface PanelRows {
    courses: CourseSummary[];
    /** group 模式:mapKey -> 组内可选课数,用于行尾 "👁 N options" */
    optionCountByKey: Map<string, number>;
    /** group 模式:mapKey -> 该行代表的整组,用于打开全部课程弹窗 */
    groupByKey: Map<string, CourseSet>;
    /** true = 节点是 Select,按组代表课展示(GROUP/OPTION_GROUP) */
    groupMode: boolean;
}

interface BuildPanelRowsOptions {
    /** LIST 专用:课程很多且 intro 课多时优先展示 1/2 级课程 */
    introHeuristic?: boolean;
}

/**
 * 根据面板节点类型计算要展示的课程行:
 *  - SELECT  → 每组一行代表课(getGroupRepresentatives),带 optionCount/groupByKey
 *  - COURSE_SET → 拍平的课程列表
 */
export function buildPanelRows(
    node: PanelNode,
    coursesMap: Record<string, CourseSummary>,
    relevantSubjects: string[],
    options?: BuildPanelRowsOptions
): PanelRows {
    const optionCountByKey = new Map<string, number>();
    const groupByKey = new Map<string, CourseSet>();

    if (node.type === "SELECT") {
        const courseSets = node.children.filter(
            (child): child is CourseSet => child.type === "COURSE_SET"
        );
        const slots = getGroupRepresentatives(courseSets, coursesMap, relevantSubjects);
        for (const slot of slots) {
            optionCountByKey.set(slot.course.mapKey, slot.optionCount);
            groupByKey.set(slot.course.mapKey, slot.courseSet);
        }
        return { courses: slots.map(slot => slot.course), optionCountByKey, groupByKey, groupMode: true };
    }

    const courses = getCoursesFromCourseSet(node, coursesMap, relevantSubjects)
        .filter(c => c.type !== 'EXAM');
    const sorted = options?.introHeuristic
        ? sortCourses(courses, shouldPrioritizeIntro(courses) ? { prioritizeLevels: [1, 2] } : { prioritizeUG: true })
        : sortCourses(courses, { prioritizeUG: true });
    return { courses: sorted, optionCountByKey, groupByKey, groupMode: false };
}

// LIST 的排序启发式:剩余可选课超过 50 门且其中 1/2 级课超过 10 门时,优先把 intro 课排前面
function shouldPrioritizeIntro(courses: CourseSummary[]): boolean {
    const eligible = courses.filter(c => c.userState.status !== 'COMPLETED');
    return eligible.length > 50
        && eligible.filter(c => c.courseInfo.level === 1 || c.courseInfo.level === 2).length > 10;
}
